import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Crown, Menu, User, LogOut, LayoutDashboard, Shield, Ticket, Gift } from "lucide-react";

const NAV = [
  { label: "Rifas Ativas", to: "/rifas" },
  { label: "Ganhadores", to: "/ganhadores" },
  { label: "Como Funciona", to: "/como-funciona" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-lg gold-btn flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </span>
          <div className="flex flex-col leading-none">
            <span className="text-xs uppercase tracking-[0.2em] text-amber-200/70">Rifa</span>
            <span className="heading text-lg font-extrabold gold-gradient-text">RMC</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace('/', '')}`}
              className="text-sm text-slate-300 hover:text-amber-200 transition-colors"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="user-menu-trigger" className="text-slate-200 hover:text-amber-200 gap-2">
                  <span className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-200 text-sm font-semibold">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                  <span className="hidden sm:inline text-sm">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-900 border-white/10 text-slate-100">
                <DropdownMenuLabel className="text-amber-200">Minha conta</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="focus:bg-white/5">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Meu Painel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard?tab=cotas")} className="focus:bg-white/5">
                  <Ticket className="w-4 h-4 mr-2" /> Minhas Cotas
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-afiliados" onClick={() => navigate("/afiliados")} className="focus:bg-white/5">
                  <Gift className="w-4 h-4 mr-2 text-emerald-300" /> Afiliados
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="focus:bg-white/5">
                    <Shield className="w-4 h-4 mr-2 text-amber-300" /> Área do Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="focus:bg-white/5 text-red-300">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="ghost" data-testid="login-header-btn" onClick={() => navigate("/login")} className="text-slate-200 hover:text-amber-200">
                Entrar
              </Button>
              <Button data-testid="register-header-btn" onClick={() => navigate("/register")} className="gold-btn rounded-full px-5">
                Criar conta
              </Button>
            </div>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-slate-200" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-slate-950 border-l border-white/10 text-slate-100 w-72">
              <div className="mt-8 flex flex-col gap-3">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg hover:bg-white/5">
                    {n.label}
                  </Link>
                ))}
                {!user && (
                  <>
                    <Button onClick={() => { setOpen(false); navigate("/login"); }} variant="outline" className="mt-2 border-white/10 bg-transparent text-slate-100">
                      Entrar
                    </Button>
                    <Button onClick={() => { setOpen(false); navigate("/register"); }} className="gold-btn">
                      Criar conta
                    </Button>
                  </>
                )}
                {user && (
                  <>
                    <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} variant="outline" className="border-white/10 bg-transparent text-slate-100">
                      <User className="w-4 h-4 mr-2" /> Meu Painel
                    </Button>
                    {user.role === "admin" && (
                      <Button onClick={() => { setOpen(false); navigate("/admin"); }} className="gold-btn">
                        <Shield className="w-4 h-4 mr-2" /> Admin
                      </Button>
                    )}
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
